import React, { createContext, useContext, useState, useEffect } from "react";
import { useUser, fetchWithAuth } from "./UserContext";
import API_URL from "../config";
import { toast } from "sonner";

// Creamos el contexto
const CompanyContext = createContext({
  companies: [],
  loading: true,
  fetchCompanies: () => {},
}); // Valor por defecto para evitar undefined

// Hook personalizado para usar el contexto
export const useCompany = () => {
  return useContext(CompanyContext);
};

// Proveedor del contexto
export const CompanyProvider = ({ children }) => {
  const { user, fetchUserInfo } = useUser();
  const [companies, setCompanies] = useState([]); // Estado para almacenar las empresas
  const [loading, setLoading] = useState(true); // Estado para mostrar que está cargando

  const fetchCompanies = async () => {
    try {
      setLoading(true);

      // Hacemos la petición al backend para obtener las empresas asociadas
      const response = await fetchWithAuth(`${API_URL}/bs/associated_company`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Verificamos si la respuesta fue exitosa
      if (response.ok) {
        const companiesData = await response.json();
        setCompanies(companiesData);
      } else {
        throw new Error("Error al obtener las empresas asociadas");
      }
    } catch (error) {
      console.error("Error al obtener la información de las empresas:", error);
    } finally {
      setLoading(false);
    }
  };

  // Nueva función para guardar cambios
  const handleSaveChanges = async (editFormData, id_company) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/bs/company-profile/${id_company}/update`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editFormData),
        }
      );

      if (response.ok) {
        toast.success("Datos actualizados exitosamente");
        fetchCompanies(); // Actualizamos la lista de empresas
      } else {
        throw new Error("Error al actualizar los datos.");
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  // Nueva función para disociar empresa
  const handleDissociate = async (companyId) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/bs/company/${companyId}/remove_user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ company_id: companyId }),
        }
      );

      if (response.ok) {
        const data = await response.json();

        fetchUserInfo();
        setCompanies(null); // O puedes usar setCompanies([]) si es un array
        toast.success(data.message);
        // 🔄 Recargar la página después de una respuesta exitosa
        setTimeout(() => {
          window.location.reload();
        }, 500); // Espera 1 segundo para mostrar el mensaje antes de recargar
      } else {
        const errorData = await response.json();
        toast.error(
          errorData.error || "Hubo un error al intentar realizar la acción."
        );
      }
    } catch (error) {
      toast.error("Error en la solicitud. Inténtalo de nuevo más tarde.");
    }
  };

  useEffect(() => {
    if (user?.companyUser?.id_company) {
      // ✅ Solo ejecuta si el usuario tiene una empresa asociada
      fetchCompanies();
    }
  }, [user]); // 🔹 Se ejecuta solo si `user` cambia

  return (
    <CompanyContext.Provider
      value={{
        companies,
        loading,
        fetchCompanies,
        handleDissociate,
        handleSaveChanges,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};
